// ============================================================
// Chess Arena — Piece Manager
// ============================================================
// Single source of truth for all pieces on the board.
// Owns the pieces Map, spawn/capture/move/collision logic.
// ============================================================

import Phaser from 'phaser';
import {
  GridPos,
  PieceType,
  PieceState,
  Team,
  LevelConfig,
  BOARD_COLS,
  BOARD_ROWS,
} from '../core/types';
import { Board } from '../core/board';
import { Piece } from '../core/piece';
import { getValidMoves } from '../core/moveValidator';
import { checkCollisions } from '../core/collisionResolver';
import { MovementLineManager } from './movementLineManager';
import eventBus from '../core/eventBus';
import { getAttackedCellsForPiece } from './checkDetector';

// -----------------------------------------------------------------
// Config passed at init
// -----------------------------------------------------------------

export interface PieceManagerConfig {
  scene: Phaser.Scene;
  board: Board;
  movementLines: MovementLineManager;
  cellSize: number;
  cellH: number;
  boardOriginX: number;
  boardOriginY: number;
  onMatchResult: (result: 'victory' | 'defeat') => void;
}

// -----------------------------------------------------------------
// PieceManager
// -----------------------------------------------------------------

export class PieceManager {
  private scene!: Phaser.Scene;
  private board!: Board;
  private movementLines!: MovementLineManager;
  private cellSize = 96;
  private cellH = 91;
  private boardOriginX = 0;
  private boardOriginY = 0;
  private onMatchResult!: (result: 'victory' | 'defeat') => void;

  // Pieces registry
  private pieces: Map<string, Piece> = new Map();
  private pieceIdCounter = 0;

  // Tutorial tracking (exposed for SpotlightResolver)
  lastMovedPieceId: string | null = null;

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  init(config: PieceManagerConfig): void {
    this.scene = config.scene;
    this.board = config.board;
    this.movementLines = config.movementLines;
    this.cellSize = config.cellSize;
    this.cellH = config.cellH;
    this.boardOriginX = config.boardOriginX;
    this.boardOriginY = config.boardOriginY;
    this.onMatchResult = config.onMatchResult;
    this.pieces.clear();
    this.pieceIdCounter = 0;
    this.lastMovedPieceId = null;
  }

  reset(): void {
    this.pieces.clear();
    this.pieceIdCounter = 0;
    this.lastMovedPieceId = null;
  }

  // ------------------------------------------------------------------
  // Spawn — two variants
  // ------------------------------------------------------------------

  /**
   * Spawn a piece and emit piece_spawned event.
   * Used for direct spawns (starting pieces, etc.).
   */
  spawnPiece(type: PieceType, team: Team, pos: GridPos): Piece {
    const piece = this.createPiece(type, team, pos);
    eventBus.emit('piece_spawned', { pieceId: piece.pieceState.id, type, team, pos });
    return piece;
  }

  /**
   * Create a piece WITHOUT emitting piece_spawned.
   * Used by SpawnSystem/WaveSystem callbacks (they emit their own enriched events).
   */
  createPiece(type: PieceType, team: Team, pos: GridPos, spawnCooldown = 0): Piece {
    const id = `${team}_${type}_${this.pieceIdCounter++}`;

    const state: PieceState = {
      id,
      type,
      team,
      pos: { ...pos },
      cooldownRemaining: 0,
      isMoving: false,
      moveStartTime: 0,
      moveTarget: null,
      isInBush: false,
      premove: null,
      isAlive: true,
      isFrozen: false,
      frozenUntil: 0,
      isShielded: false,
      shieldedUntil: 0,
    };

    const teamColor = team === 'player' ? 'blue' : 'red';
    const spriteKey = `piece_${type.toLowerCase()}_${teamColor}`;
    const pixel = Board.gridToPixel(pos, this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH);

    const piece = new Piece(this.scene, pixel.x, pixel.y, state, spriteKey);
    const sizeMultiplier = type === 'Pawn' ? 0.68 : 0.8;
    piece.sprite.setDisplaySize(this.cellSize * sizeMultiplier, this.cellSize * sizeMultiplier);
    piece.cellSize = this.cellSize;
    piece.setDepth(10);

    // Check if spawning in a bush
    if (this.board.isBush(pos)) {
      piece.setInBush(true);
    }

    this.board.placePiece(id, pos);
    this.pieces.set(id, piece);

    // Apply spawn cooldown so newly spawned pieces can't capture immediately
    if (spawnCooldown > 0) {
      piece.startCooldown(spawnCooldown);
    }

    // Pop-in animation on the piece itself
    piece.playSpawnAnimation();

    // Spawn glow effect
    if (this.scene.textures.exists('fx_spawn_glow')) {
      const glow = this.scene.add.image(pixel.x, pixel.y, 'fx_spawn_glow');
      glow.setDisplaySize(this.cellSize, this.cellSize);
      glow.setAlpha(0.7);
      glow.setDepth(9);
      this.scene.tweens.add({
        targets: glow,
        alpha: 0,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 400,
        onComplete: () => glow.destroy(),
      });
    }

    return piece;
  }

  // ------------------------------------------------------------------
  // Piece placement (starting armies)
  // ------------------------------------------------------------------

  placeStartingPieces(levelConfig: LevelConfig): void {
    if (levelConfig.level === 1 && levelConfig.tutorialLevel) {
      // Tutorial Level 1: scripted placement (5x7 board)
      this.spawnPiece('King', 'player', { col: 1, row: BOARD_ROWS - 1 });
      this.spawnPiece('Knight', 'player', { col: 2, row: BOARD_ROWS - 2 });
      this.spawnPiece('King', 'enemy', { col: 1, row: BOARD_ROWS - 3 });
    } else if (levelConfig.level === 2 && levelConfig.tutorialLevel) {
      // Tutorial Level 2 (bomb): enemies clustered in 2×2 for rigged bomb
      // Enemy King exposed at (2,2), cluster at cols 1-2 rows 3-4
      // Player Knight at (3,4) can L-move to (2,2) after bomb clears cluster
      this.spawnPiece('King', 'player', { col: 2, row: BOARD_ROWS - 1 });
      this.spawnPiece('Knight', 'player', { col: 3, row: 4 });
      this.spawnPiece('King', 'enemy', { col: 2, row: 2 });
      this.spawnPiece('Queen', 'enemy', { col: 1, row: 3 });
      this.spawnPiece('Rook', 'enemy', { col: 2, row: 3 });
      this.spawnPiece('Knight', 'enemy', { col: 1, row: 4 });
      this.spawnPiece('Bishop', 'enemy', { col: 2, row: 4 });
    } else if (levelConfig.level === 3 && levelConfig.tutorialLevel) {
      // Tutorial Level 3 (premove): scripted placement (5x7 board)
      // Knight at center, enemy King 2 L-moves away so premove captures it
      this.spawnPiece('King', 'player', { col: 0, row: BOARD_ROWS - 1 });
      this.spawnPiece('Knight', 'player', { col: 2, row: BOARD_ROWS - 2 });
      this.spawnPiece('King', 'enemy', { col: 2, row: 1 });
    } else if (levelConfig.level === 4 && levelConfig.tutorialLevel) {
      // Tutorial Level 4 (bush ambush): scripted placement (5x7 board)
      this.spawnPiece('King', 'player', { col: 3, row: BOARD_ROWS - 3 });
      this.spawnPiece('Pawn', 'player', { col: 1, row: BOARD_ROWS - 1 });
      this.spawnPiece('King', 'enemy', { col: 3, row: 2 });
    } else {
      this.placeArmy(levelConfig.playerArmy, 'player', [BOARD_ROWS - 2, BOARD_ROWS - 1]);
      // Skip enemy King when it will be spawned later by the wave system
      const skipEnemyKing = !levelConfig.enemyKingVisible && levelConfig.enemyKingAppearsAfterWave != null;
      this.placeArmy(levelConfig.enemyArmy, 'enemy', [0, 1], skipEnemyKing);
    }
  }

  private placeArmy(army: Partial<Record<PieceType, number>>, team: Team, rows: number[], skipKing = false): void {
    const backRow = team === 'player' ? rows[rows.length - 1] : rows[0];
    const emptyCells = this.board.getEmptyCells([rows[0], rows[rows.length - 1]]);

    // Place King first, always in the back row
    if (army.King && !skipKing) {
      const backRowCells = emptyCells.filter(c => c.row === backRow);
      if (backRowCells.length > 0) {
        const kingPos = backRowCells[Math.floor(Math.random() * backRowCells.length)];
        this.spawnPiece('King', team, kingPos);
        const idx = emptyCells.findIndex(c => c.col === kingPos.col && c.row === kingPos.row);
        if (idx !== -1) emptyCells.splice(idx, 1);
      }
    }

    // Place remaining pieces
    let cellIndex = 0;
    for (const [type, count] of Object.entries(army)) {
      if (type === 'King') continue;
      for (let i = 0; i < (count as number); i++) {
        if (cellIndex >= emptyCells.length) break;
        const pos = emptyCells[cellIndex++];
        this.spawnPiece(type as PieceType, team, pos);
      }
    }
  }

  // ------------------------------------------------------------------
  // Capture
  // ------------------------------------------------------------------

  capturePiece(attacker: Piece, target: Piece): void {
    const targetType = target.pieceState.type;
    const targetTeam = target.pieceState.team;

    // Shield check: if the target King is shielded, block the capture
    if (targetType === 'King' && target.pieceState.isShielded) {
      const pixel = { x: target.x, y: target.y };
      const blockGfx = this.scene.add.graphics();
      blockGfx.fillStyle(0xffd700, 0.5);
      blockGfx.fillCircle(pixel.x, pixel.y, this.cellSize * 0.7);
      blockGfx.setDepth(20);
      this.scene.tweens.add({
        targets: blockGfx,
        alpha: 0,
        duration: 400,
        onComplete: () => blockGfx.destroy(),
      });
      return;
    }

    // Remove from board
    this.board.removePiece(target.pieceState.pos);
    this.pieces.delete(target.pieceState.id);
    target.pieceState.moveTarget = null;

    // Clean up movement line and premove ghost
    this.movementLines.removeLine(target.pieceState.id);
    target.setPremove(null);

    // Play capture animation
    target.capture();

    // Capture flash effect
    const pixel = { x: target.x, y: target.y };
    if (this.scene.textures.exists('fx_capture_flash')) {
      const flash = this.scene.add.image(pixel.x, pixel.y, 'fx_capture_flash');
      flash.setDisplaySize(this.cellSize * 1.2, this.cellSize * 1.2);
      flash.setAlpha(0.9);
      flash.setDepth(15);
      this.scene.tweens.add({
        targets: flash,
        alpha: 0,
        scaleX: 2,
        scaleY: 2,
        duration: 300,
        onComplete: () => flash.destroy(),
      });
    }

    // Emit capture event
    eventBus.emit('piece_captured', {
      pieceType: targetType,
      team: targetTeam,
      attackerId: attacker.pieceState.id,
    });

    // King capture → match result
    if (targetType === 'King') {
      eventBus.emit('king_captured', { capturedTeam: targetTeam, attackerId: attacker.pieceState.id });
      this.onMatchResult(targetTeam === 'enemy' ? 'victory' : 'defeat');
    }
  }

  // ------------------------------------------------------------------
  // Move execution
  // ------------------------------------------------------------------

  executeMove(piece: Piece, targetPos: GridPos): void {
    const fromPos = { ...piece.pieceState.pos };

    // Ignore no-op moves
    if (fromPos.col === targetPos.col && fromPos.row === targetPos.row) return;

    const isKnight = piece.pieceState.type === 'Knight';

    // Remove piece from origin cell — it is now "in transit"
    this.board.removePiece(fromPos);
    piece.pieceState.moveTarget = { ...targetPos };

    // Handle bush stealth (leaving)
    if (this.board.isBush(fromPos)) {
      piece.setInBush(false);
    }

    // Animate the move
    const targetPixel = Board.gridToPixel(targetPos, this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH);
    piece.startMove(targetPixel, this.cellSize, isKnight);

    // Track for tutorial spotlight
    this.lastMovedPieceId = piece.pieceState.id;

    eventBus.emit('piece_moved', {
      pieceId: piece.pieceState.id,
      from: fromPos,
      to: targetPos,
      type: piece.pieceState.type,
      team: piece.pieceState.team,
    });

    // Show movement trajectory line
    this.movementLines.addLine(piece.pieceState.id, fromPos, targetPos, piece.pieceState.team);
  }

  /** Execute a tutorial-scripted move: resolve a piece identifier and move it. */
  executeScriptedMove(pieceTarget: string, destination: GridPos): void {
    let targetPiece: Piece | undefined;

    for (const piece of this.pieces.values()) {
      if (!piece.pieceState.isAlive) continue;
      if (pieceTarget === 'enemyKing' && piece.pieceState.team === 'enemy' && piece.pieceState.type === 'King') {
        targetPiece = piece;
        break;
      }
      if (pieceTarget === 'enemyPawn' && piece.pieceState.team === 'enemy' && piece.pieceState.type === 'Pawn') {
        targetPiece = piece;
        break;
      }
    }

    if (targetPiece) {
      this.executeMove(targetPiece, destination);
    }
  }

  // ------------------------------------------------------------------
  // Move arrival
  // ------------------------------------------------------------------

  resolveMoveArrival(piece: Piece): void {
    const targetPos = piece.pieceState.moveTarget;
    if (!targetPos) return;

    // Fade out the movement trajectory line
    this.movementLines.removeLine(piece.pieceState.id);
    piece.pieceState.moveTarget = null;

    // Check if target cell has an enemy piece — capture on arrival
    const targetCell = this.board.getCellContent(targetPos);
    if (targetCell && targetCell.pieceId) {
      const targetPiece = this.pieces.get(targetCell.pieceId);
      if (targetPiece && targetPiece.pieceState.isAlive) {
        if (targetPiece.pieceState.team !== piece.pieceState.team) {
          this.capturePiece(piece, targetPiece);
        } else {
          // Friendly piece now occupies the target — find nearest empty cell
          const fallback = this.findNearestEmptyCell(targetPos);
          if (fallback) {
            const fallbackPixel = Board.gridToPixel(fallback, this.cellSize, this.boardOriginX, this.boardOriginY, this.cellH);
            piece.x = fallbackPixel.x;
            piece.y = fallbackPixel.y;
            this.board.placePiece(piece.pieceState.id, fallback);
            piece.pieceState.pos = { ...fallback };
            if (this.board.isBush(fallback)) {
              piece.setInBush(true);
            }
            return;
          }
        }
      }
    }

    // Place piece at target cell
    this.board.placePiece(piece.pieceState.id, targetPos);
    piece.pieceState.pos = { ...targetPos };

    // Handle bush stealth (arriving)
    if (this.board.isBush(targetPos)) {
      piece.setInBush(true);
    }
  }

  // ------------------------------------------------------------------
  // Collision detection
  // ------------------------------------------------------------------

  updateCollisions(): void {
    const allAlive: Piece[] = [];
    const moving: Piece[] = [];
    for (const piece of this.pieces.values()) {
      if (!piece.pieceState.isAlive) continue;
      allAlive.push(piece);
      if (piece.pieceState.isMoving) moving.push(piece);
    }
    if (moving.length === 0) return;

    const captures = checkCollisions(
      moving,
      allAlive,
      () => this.cellSize,
      () => ({ x: this.boardOriginX, y: this.boardOriginY }),
    );

    for (const cap of captures) {
      const winner = this.pieces.get(cap.winnerId);
      const loser = this.pieces.get(cap.loserId);
      if (winner && loser && winner.pieceState.isAlive && loser.pieceState.isAlive) {
        this.capturePiece(winner, loser);
      }
    }
  }

  // ------------------------------------------------------------------
  // Win condition check (safety fallback)
  // ------------------------------------------------------------------

  checkWinCondition(): boolean {
    let playerKingAlive = false;
    let enemyKingAlive = false;

    for (const piece of this.pieces.values()) {
      if (!piece.pieceState.isAlive) continue;
      if (piece.pieceState.type === 'King') {
        if (piece.pieceState.team === 'player') playerKingAlive = true;
        if (piece.pieceState.team === 'enemy') enemyKingAlive = true;
      }
    }

    if (!enemyKingAlive) {
      this.onMatchResult('victory');
      return true;
    }
    if (!playerKingAlive) {
      this.onMatchResult('defeat');
      return true;
    }

    return false;
  }

  // ------------------------------------------------------------------
  // Queries
  // ------------------------------------------------------------------

  /** Get a piece by its ID. */
  getPieceById(id: string): Piece | undefined {
    return this.pieces.get(id);
  }

  /** Return alive pieces for a team. */
  getPieces(team: Team): PieceState[] {
    const result: PieceState[] = [];
    for (const piece of this.pieces.values()) {
      if (piece.pieceState.isAlive && piece.pieceState.team === team) {
        result.push(piece.pieceState);
      }
    }
    return result;
  }

  /** Get all alive piece states. */
  getAllPieceStates(): PieceState[] {
    const states: PieceState[] = [];
    for (const piece of this.pieces.values()) {
      if (piece.pieceState.isAlive) {
        states.push(piece.pieceState);
      }
    }
    return states;
  }

  /** Count alive pieces belonging to a team. */
  countAlivePieces(team: Team): number {
    let count = 0;
    for (const piece of this.pieces.values()) {
      if (piece.pieceState.isAlive && piece.pieceState.team === team) count++;
    }
    return count;
  }

  /** Find the player's King piece. */
  findPlayerKing(): Piece | null {
    for (const piece of this.pieces.values()) {
      if (piece.pieceState.team === 'player' && piece.pieceState.type === 'King' && piece.pieceState.isAlive) {
        return piece;
      }
    }
    return null;
  }

  /** Iterate all pieces (for ability effects, etc.). */
  forEachPiece(fn: (piece: Piece) => void): void {
    for (const piece of this.pieces.values()) {
      fn(piece);
    }
  }

  /**
   * Remove a piece from the board and pieces map without full capture flow.
   * Used by bomb explosion and lightning effects that handle their own VFX/events.
   */
  removePiece(piece: Piece): void {
    this.board.removePiece(piece.pieceState.pos);
    this.pieces.delete(piece.pieceState.id);
    piece.pieceState.moveTarget = null;
    this.movementLines.removeLine(piece.pieceState.id);
    piece.setPremove(null);
  }

  /** Iterate all pieces (alive and dead). Returns the raw iterable. */
  allPieces(): IterableIterator<Piece> {
    return this.pieces.values();
  }

  /** Find the nearest empty, passable cell to the given position. */
  findNearestEmptyCell(pos: GridPos): GridPos | null {
    const deltas = [
      { col: 0, row: -1 }, { col: 0, row: 1 },
      { col: -1, row: 0 }, { col: 1, row: 0 },
      { col: -1, row: -1 }, { col: 1, row: -1 },
      { col: -1, row: 1 }, { col: 1, row: 1 },
    ];
    for (const d of deltas) {
      const candidate = { col: pos.col + d.col, row: pos.row + d.row };
      if (!this.board.isValidPos(candidate)) continue;
      if (!this.board.isPassable(candidate)) continue;
      const cell = this.board.getCellContent(candidate);
      if (cell && !cell.pieceId) return candidate;
    }
    return null;
  }

  /** Update cooldowns for all alive pieces. */
  updateCooldowns(delta: number): void {
    for (const piece of this.pieces.values()) {
      if (piece.pieceState.isAlive) {
        piece.updateCooldown(delta);
      }
    }
    this.updateCheckIndicators();
  }

  /** Check if any king is under threat and update visual indicators. */
  private updateCheckIndicators(): void {
    const allStates = this.getAllPieceStates();

    for (const piece of this.pieces.values()) {
      if (!piece.pieceState.isAlive || piece.pieceState.type !== 'King') continue;
      if (piece.pieceState.isMoving) {
        piece.setInCheck(false);
        continue;
      }

      const kingPos = piece.pieceState.pos;
      const enemyTeam = piece.pieceState.team === 'player' ? 'enemy' : 'player';
      let inCheck = false;

      for (const other of allStates) {
        if (other.team !== enemyTeam || !other.isAlive || other.isMoving) continue;
        const attacked = getAttackedCellsForPiece(other, this.board);
        if (attacked.some(c => c.col === kingPos.col && c.row === kingPos.row)) {
          inCheck = true;
          break;
        }
      }

      piece.setInCheck(inCheck);
      piece.updateCheckIndicatorPosition();
    }
  }

  /** Get board reference (for external systems). */
  getBoard(): Board {
    return this.board;
  }

  getCellSize(): number {
    return this.cellSize;
  }

  getCellH(): number {
    return this.cellH;
  }

  getBoardOriginX(): number {
    return this.boardOriginX;
  }

  getBoardOriginY(): number {
    return this.boardOriginY;
  }
}
